import { useQuery } from '@tanstack/react-query'
import { Input, Select } from '../../components/ui/Input'
import { usersApi } from '../../api/users.api'
import { rolesApi } from '../../api/roles.api'
import { TEMPLATE_VARIABLES } from './automationLabels'
import type { ActionType, DiscordChannelKey } from '../../api/types'

/** Aufklappbare Referenz für {{...}}-Variablen — nur bei Aktionstypen relevant, die
 * Templates interpolieren (Discord-Nachricht, Panel-Benachrichtigung, Reminder, Kommentar). */
function TemplateVariablesHint() {
  return (
    <details style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
      <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)', userSelect: 'none' }}>Verfügbare Variablen anzeigen</summary>
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3, paddingLeft: 2 }}>
        {TEMPLATE_VARIABLES.map((v) => (
          <div key={v.key}>
            <code style={{ color: 'var(--accent-10)' }}>{`{{${v.key}}}`}</code> — {v.description}
          </div>
        ))}
      </div>
    </details>
  )
}

const DISCORD_CHANNEL_KEYS: DiscordChannelKey[] = [
  'TASKS',
  'TASKS_IMPORTANT',
  'PROBLEMS',
  'ROADMAP',
  'LOGS',
  'REMINDERS',
  'PROJECT_UPDATES',
  'SYSTEM',
]

interface ActionParamsFieldsProps {
  type: ActionType
  params: Record<string, unknown>
  onChange: (params: Record<string, unknown>) => void
}

export function ActionParamsFields({ type, params, onChange }: ActionParamsFieldsProps) {
  const usersQuery = useQuery({ queryKey: ['users', { pageSize: 100 }], queryFn: async () => (await usersApi.list({ pageSize: 100 })).data })
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: async () => (await rolesApi.list()).data })

  const set = (key: string, value: unknown) => onChange({ ...params, [key]: value })
  const str = (key: string) => (typeof params[key] === 'string' ? (params[key] as string) : '')

  switch (type) {
    case 'change_status':
      return (
        <Input placeholder="Status-Name, z.B. Erledigt" value={str('statusName')} onChange={(e) => set('statusName', e.target.value)} />
      )

    case 'change_priority':
      return (
        <Select value={str('priority')} onChange={(e) => set('priority', e.target.value)}>
          <option value="">Priorität wählen…</option>
          <option value="LOW">Niedrig</option>
          <option value="MEDIUM">Mittel</option>
          <option value="HIGH">Hoch</option>
          <option value="CRITICAL">Kritisch</option>
        </Select>
      )

    case 'add_label':
    case 'remove_label':
      return <Input placeholder="Label-Name, z.B. Wichtig" value={str('labelName')} onChange={(e) => set('labelName', e.target.value)} />

    case 'assign_user':
      return (
        <Select value={str('userId')} onChange={(e) => set('userId', e.target.value)}>
          <option value="">Benutzer wählen…</option>
          {usersQuery.data?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </Select>
      )

    case 'assign_role':
      return (
        <Select value={str('roleId')} onChange={(e) => set('roleId', e.target.value)}>
          <option value="">Rolle wählen…</option>
          {rolesQuery.data?.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
      )

    case 'send_discord_message':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Select value={str('channelKey')} onChange={(e) => set('channelKey', e.target.value)} style={{ maxWidth: 180 }}>
              <option value="">Channel…</option>
              {DISCORD_CHANNEL_KEYS.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </Select>
            <Input placeholder="Nachricht, z.B. {{task.title}} ist fällig" value={str('template')} onChange={(e) => set('template', e.target.value)} />
          </div>
          <TemplateVariablesHint />
        </div>
      )

    case 'send_panel_notification':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Select value={str('userId')} onChange={(e) => onChange({ ...params, userId: e.target.value, roleId: undefined })} style={{ maxWidth: 220 }}>
              <option value="">Bestimmter Benutzer…</option>
              {usersQuery.data?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName}
                </option>
              ))}
            </Select>
            <Select value={str('roleId')} onChange={(e) => onChange({ ...params, roleId: e.target.value, userId: undefined })} style={{ maxWidth: 220 }}>
              <option value="">Oder: alle mit Rolle…</option>
              {rolesQuery.data?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </div>
          <Input placeholder="Titel" value={str('title')} onChange={(e) => set('title', e.target.value)} />
          <Input placeholder="Text, z.B. {{task.title}} braucht Aufmerksamkeit" value={str('body')} onChange={(e) => set('body', e.target.value)} />
          <TemplateVariablesHint />
        </div>
      )

    case 'create_reminder':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Input placeholder="Nachricht" value={str('message')} onChange={(e) => set('message', e.target.value)} />
            <Input
              type="number"
              placeholder="Fällig in (Min.)"
              style={{ maxWidth: 140 }}
              value={typeof params.dueInMinutes === 'number' ? params.dueInMinutes : ''}
              onChange={(e) => set('dueInMinutes', Number(e.target.value))}
            />
          </div>
          <TemplateVariablesHint />
        </div>
      )

    case 'add_comment':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Input placeholder="Kommentartext, z.B. Automatisch: {{task.title}}" value={str('body')} onChange={(e) => set('body', e.target.value)} />
          <TemplateVariablesHint />
        </div>
      )

    default:
      return null
  }
}
